import React, { useEffect, useState, useMemo } from "react";
import { useLang } from "../../contexts/LanguageContext";
import { api, API, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Download } from "lucide-react";

const STATUSES = ["new", "contacted", "converted", "archived"];

export default function AdminInquiries() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [countryFilter, setCountryFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get("/inquiries").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter(i =>
    (statusFilter === "all" || i.status === statusFilter) &&
    (countryFilter === "all" || i.country === countryFilter) &&
    (!search || (i.parent_first_name + " " + i.parent_last_name + " " + i.email).toLowerCase().includes(search.toLowerCase()))
  ), [items, statusFilter, countryFilter, search]);

  const countries = [...new Set(items.map(i => i.country).filter(Boolean))];

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status, notes: note || undefined });
      toast.success("Updated");
      setNote("");
      setOpen(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const convert = async (id) => {
    try {
      const { data } = await api.post(`/inquiries/${id}/convert`);
      toast.success(`Application ${data.application_id.slice(0, 8)} created`);
      setOpen(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("ofa_token");
    const res = await fetch(`${API}/inquiries/export`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "inquiries.csv"; a.click();
  };

  return (
    <div data-testid="admin-inquiries">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="overline mb-1">OFA France · Admin</p>
          <h1 className="font-serif text-3xl tracking-tight">{t("admin.inquiries")}</h1>
        </div>
        <Button data-testid="export-csv" variant="outline" onClick={exportCsv}><Download className="w-4 h-4 mr-2" />{t("common.export")}</Button>
      </div>

      <Card className="mb-4">
        <CardContent className="p-4 grid md:grid-cols-4 gap-3">
          <Input data-testid="search-input" placeholder={t("common.search")} value={search} onChange={(e) => setSearch(e.target.value)} />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              {STATUSES.map(s => <SelectItem key={s} value={s}>{t(`statuses.${s}`)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger data-testid="filter-country"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.all")}</SelectItem>
              {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-sm text-muted-foreground self-center">{filtered.length} / {items.length}</div>
        </CardContent>
      </Card>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left p-3">Parent</th>
                <th className="text-left p-3">Child</th>
                <th className="text-left p-3">Country</th>
                <th className="text-left p-3">Grade</th>
                <th className="text-left p-3">Program</th>
                <th className="text-left p-3">Lang</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(i => (
                <tr key={i.id} data-testid={`inquiry-row-${i.id}`} onClick={() => setOpen(i)} className="border-t border-border hover:bg-secondary/30 cursor-pointer">
                  <td className="p-3"><div className="font-medium">{i.parent_first_name} {i.parent_last_name}</div><div className="text-xs text-muted-foreground">{i.email}</div></td>
                  <td className="p-3">{i.child_first_name} {i.child_last_name}</td>
                  <td className="p-3 text-xs">{i.country}</td>
                  <td className="p-3 text-xs">{i.current_grade}</td>
                  <td className="p-3 text-xs">{i.program_interest ? t(`programs.${i.program_interest}`) : "—"}</td>
                  <td className="p-3 text-xs uppercase">{i.language}</td>
                  <td className="p-3"><Badge>{t(`statuses.${i.status}`)}</Badge></td>
                  <td className="p-3 text-xs text-muted-foreground">{new Date(i.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-muted-foreground">No results</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-2xl">
          {open && (
            <>
              <DialogHeader><DialogTitle className="font-serif text-2xl">{open.parent_first_name} {open.parent_last_name}</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <Row k="Email" v={open.email} />
                <Row k="Phone" v={open.phone} />
                <Row k="Country" v={open.country} />
                <Row k="Child" v={`${open.child_first_name} ${open.child_last_name} (${open.current_grade || '—'})`} />
                <Row k="Program" v={open.program_interest ? t(`programs.${open.program_interest}`) : "—"} />
                <Row k="Goal" v={open.main_goal ? t(`programs.${open.main_goal}`) : "—"} />
                <Row k="French" v={open.french_level} />
                <Row k="English" v={open.english_level} />
                {open.message && <div className="bg-secondary/40 p-3 rounded text-xs">{open.message}</div>}
                <div>
                  <label className="text-xs text-muted-foreground">{t("admin.addNote")}</label>
                  <Textarea data-testid="inquiry-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  {STATUSES.map(s => (
                    <Button key={s} size="sm" variant={open.status === s ? "default" : "outline"} data-testid={`set-status-${s}`} onClick={() => setStatus(open.id, s)}>{t(`statuses.${s}`)}</Button>
                  ))}
                  {open.status !== "converted" && (
                    <Button size="sm" data-testid="convert-application" className="bg-accent text-accent-foreground hover:bg-accent/90" onClick={() => convert(open.id)}>{t("admin.convertToApplication")}</Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between border-b border-border/50 py-1.5"><span className="text-muted-foreground">{k}</span><span>{v || "—"}</span></div>;
}
